import RecoveryHelp from "../securityRecovery/RecoveryHelp";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  getOperationsUser,
  operationsLogin,
  startOperations2FAEnrollment,
  confirmOperations2FAEnrollment,
  verifyOperations2FA,
} from "./operationsApi";
import "./operations.css";

export default function OperationsLogin() {
  // The existing React route uses restaurantId; its value is the restaurant slug.
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [method, setMethod] = useState("authenticator");
  const [step, setStep] = useState("password");
  const [configUrl, setConfigUrl] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [codesSaved, setCodesSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");
  const dashboard = `/operations/${restaurantId}/dashboard`;

  useEffect(() => {
    let active = true;
    getOperationsUser(restaurantId)
      .then(() => { if (active) navigate(dashboard, { replace: true }); })
      .catch(() => {})
      .finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [restaurantId, dashboard, navigate]);

  async function perform(action) {
    if (loading) return;
    setError("");
    setLoading(true);
    try { await action(); }
    catch (err) {
      setError(err.message || "Request failed. Please try again.");
      if (err.status === 401 && step !== "password") {
        setStep("password");
        setOtp("");
        setRecoveryCode("");
        setConfigUrl("");
      }
    } finally { setLoading(false); }
  }

  function submitPassword(event) {
    event.preventDefault();
    perform(async () => {
      const result = await operationsLogin(restaurantId, username.trim(), password);
      setPassword("");
      if (result.authenticated === true && result.requires_2fa !== true) {
        navigate(dashboard, { replace: true });
      } else if (result.requires_2fa === true) {
        setStep(result.requires_enrollment ? "enrollment" : "verification");
      } else {
        throw new Error("Unexpected login response. Please try again.");
      }
    });
  }

  function startEnrollment() {
    perform(async () => {
      const result = await startOperations2FAEnrollment(restaurantId);
      if (!result.config_url) throw new Error("The server did not return an authenticator setup URL.");
      setConfigUrl(result.config_url);
      setStep("enrollment-confirm");
    });
  }

  function confirmEnrollment(event) {
    event.preventDefault();
    perform(async () => {
      const result = await confirmOperations2FAEnrollment(restaurantId, otp.trim());
      if (!result.authenticated || !Array.isArray(result.recovery_codes)) {
        throw new Error("Authenticator setup did not complete as expected.");
      }
      setOtp("");
      setRecoveryCodes(result.recovery_codes);
      setCodesSaved(false);
      setStep("recovery-codes");
    });
  }

  function submitVerification(event) {
    event.preventDefault();
    perform(async () => {
      const payload = method === "authenticator"
        ? { token: otp.trim() }
        : { recovery_code: recoveryCode.trim() };
      const result = await verifyOperations2FA(restaurantId, payload);
      if (!result.authenticated) throw new Error("Two-factor verification was not completed.");
      setOtp("");
      setRecoveryCode("");
      navigate(dashboard, { replace: true });
    });
  }

  if (checking) {
    return <div className="operations-page"><div className="operations-login-card"><p>Checking your login...</p></div></div>;
  }

  return (
    <div className="operations-page">
      <div className="operations-login-card">
        <div className="operations-logo">🍽️</div>
        <h1>Restaurant Operations</h1>
        <p className="operations-subtitle">Owner, Manager &amp; Cashier Login</p>
        <p className="operations-restaurant">Restaurant: {restaurantId}</p>

        {step === "password" && (
          <form onSubmit={submitPassword}>
            <label htmlFor="operations-username">Username</label>
            <input id="operations-username" type="text" autoComplete="username" placeholder="Enter username" value={username} onChange={e => setUsername(e.target.value)} required />
            <label htmlFor="operations-password">Password</label>
            <input id="operations-password" type="password" autoComplete="current-password" placeholder="Enter password" value={password} onChange={e => setPassword(e.target.value)} required />
            {error && <div className="operations-error" role="alert">{error}</div>}
            <button type="submit" disabled={loading}>{loading ? "Signing in..." : "Sign In"}</button>
          </form>
        )}

        {step === "enrollment" && (
          <div>
            <h2>Set up two-factor authentication</h2>
            <p>Owners and Managers must register an authenticator app before entering the dashboard.</p>
            {error && <div className="operations-error" role="alert">{error}</div>}
            <button type="button" disabled={loading} onClick={startEnrollment}>{loading ? "Preparing..." : "Set up authenticator"}</button>
          </div>
        )}

        {step === "enrollment-confirm" && (
          <form onSubmit={confirmEnrollment}>
            <h2>Scan your QR code</h2>
            <p>Scan this QR code with your authenticator app, then enter its six-digit code.</p>
            {configUrl && <div style={{ background: "white", padding: 16, display: "inline-block", maxWidth: "100%" }}><QRCodeSVG value={configUrl} size={192} /></div>}
            <label htmlFor="operations-enroll-token">Authenticator code</label>
            <input id="operations-enroll-token" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" required />
            {error && <div className="operations-error" role="alert">{error}</div>}
            <button type="submit" disabled={loading || otp.length !== 6}>{loading ? "Verifying..." : "Verify and finish setup"}</button>
          </form>
        )}

        {step === "verification" && (
          <form onSubmit={submitVerification}>
            <h2>Two-factor verification</h2>
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <label><input type="radio" name="verification-method" checked={method === "authenticator"} onChange={() => { setMethod("authenticator"); setError(""); }} /> Authenticator</label>
              <label><input type="radio" name="verification-method" checked={method === "recovery"} onChange={() => { setMethod("recovery"); setError(""); }} /> Recovery code</label>
            </div>
            {method === "authenticator" ? (
              <><label htmlFor="operations-verify-token">Authenticator code</label><input id="operations-verify-token" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" placeholder="6-digit code" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} required /></>
            ) : (
              <><label htmlFor="operations-recovery-code">Unused recovery code</label><input id="operations-recovery-code" type="text" autoComplete="off" maxLength={8} placeholder="Recovery code" value={recoveryCode} onChange={e => setRecoveryCode(e.target.value.trim().slice(0, 8))} required /></>
            )}
            {error && <div className="operations-error" role="alert">{error}</div>}
            <button type="submit" disabled={loading || (method === "authenticator" ? otp.length !== 6 : recoveryCode.length !== 8)}>{loading ? "Verifying..." : "Verify and sign in"}</button>
          </form>
        )}

        {step === "recovery-codes" && (
          <div>
            <h2>Save your recovery codes</h2>
            <p>Each code works only once. Store them securely. They will not be displayed again.</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, margin: "16px 0" }}>
              {recoveryCodes.map((code, index) => <code key={index} style={{ overflowWrap: "anywhere" }}>{code}</code>)}
            </div>
            <button type="button" onClick={() => {
              const content = recoveryCodes.join("\n");
              if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(content).catch(() => setError("Copy failed. Save the codes manually."));
              } else { setError("Clipboard unavailable. Save the codes manually."); }
            }}>Copy recovery codes</button>
            {error && <div className="operations-error" role="alert">{error}</div>}
            <label style={{ display: "block", marginTop: 16 }}><input type="checkbox" checked={codesSaved} onChange={e => setCodesSaved(e.target.checked)} /> I have saved my recovery codes securely.</label>
            <button type="button" disabled={!codesSaved} onClick={() => navigate(dashboard, { replace: true })}>Continue to dashboard</button>
          </div>
        )}
        <RecoveryHelp audience="staff" onUseCode={() => { setMethod("recovery"); setError(""); }} />
        <p className="operations-footer">Authorized restaurant staff only</p>
      </div>
    </div>
  );
}